/**
 * Admin → IdP Connections (SAML 2.0 + OIDC)
 *
 * Per-tenant SSO configuration. Each IdP connection plugs an external
 * identity provider into the tenant's SSO surface:
 *   - SAML 2.0 (Okta, Entra, Google Workspace, ADFS, JumpCloud, Ping, OneLogin)
 *   - OIDC 1.0 with mandatory PKCE-S256 (RFC 9700)
 *
 * One row per (tenant, protocol) can be enabled at a time - disabled
 * rows are preserved for audit.
 *
 * Layout: 2-column. Left = create form (tab switch for SAML vs OIDC).
 * Right = existing connections grouped by protocol.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Clock,
  KeyRound,
  Loader2,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
  XCircle,
} from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import {
  createIdpConnection,
  deleteIdpConnection,
  listIdpConnections,
  type IdpConnection,
  type IdpProtocol,
} from '@/lib/auth-admin-api';

export function IdpConnections(): JSX.Element {
  const [connections, setConnections] = useState<IdpConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<IdpProtocol>('saml');
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listIdpConnections();
      setConnections(rows);
    } catch (err) {
      setError(String((err as Error)?.message ?? err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const samlRows = connections.filter((c) => c.protocol === 'saml');
  const oidcRows = connections.filter((c) => c.protocol === 'oidc');

  async function handleDelete(id: string): Promise<void> {
    if (!confirm('Delete this IdP connection? Existing SSO sessions remain valid until their JWTs expire.')) return;
    try {
      await deleteIdpConnection(id);
      await refresh();
    } catch (err) {
      alert(`Delete failed: ${String((err as Error)?.message ?? err)}`);
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="Identity Providers"
        subtitle="Configure SAML 2.0 and OIDC IdP connections for tenant SSO. One row per (tenant, protocol) can be enabled at a time. PKCE-S256 is enforced on every OIDC client per RFC 9700."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left - Create form */}
        <GlassCard>
          <CardHeader>
            <CardTitle>Connect an IdP</CardTitle>
            <CardDescription>
              Pick a protocol, fill the connector config, and save. Save is encrypted with the tenant's KMS key.
            </CardDescription>
          </CardHeader>
          <div className="p-5 pt-0">
            <div className="mb-4 flex gap-2">
              <Button
                size="sm"
                variant={tab === 'saml' ? 'default' : 'outline'}
                onClick={() => setTab('saml')}
              >
                SAML 2.0
              </Button>
              <Button
                size="sm"
                variant={tab === 'oidc' ? 'default' : 'outline'}
                onClick={() => setTab('oidc')}
              >
                OIDC (PKCE S256)
              </Button>
            </div>
            {tab === 'saml' ? (
              <SamlCreateForm submitting={submitting} setSubmitting={setSubmitting} onDone={refresh} />
            ) : (
              <OidcCreateForm submitting={submitting} setSubmitting={setSubmitting} onDone={refresh} />
            )}
          </div>
        </GlassCard>

        {/* Right - Existing */}
        <GlassCard>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Existing connections</CardTitle>
                <CardDescription>
                  {connections.length} total · {samlRows.length} SAML · {oidcRows.length} OIDC
                </CardDescription>
              </div>
              <Button size="sm" variant="ghost" onClick={refresh} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
            </div>
          </CardHeader>
          <div className="p-5 pt-0">
            {error && (
              <div className="mb-3 rounded-md bg-red-500/10 border border-red-500/30 p-3 text-xs text-red-500">
                <AlertTriangle className="-mt-0.5 mr-1 inline h-3 w-3" />
                {error}
              </div>
            )}
            {connections.length === 0 && !loading && (
              <div className="text-xs text-muted-foreground italic">No IdP connections configured.</div>
            )}
            <div className="space-y-3">
              {connections.map((c) => (
                <ConnectionRow key={c.id} c={c} onDelete={handleDelete} />
              ))}
            </div>
          </div>
        </GlassCard>
      </div>
    </PageContainer>
  );
}

function ConnectionRow({ c, onDelete }: { c: IdpConnection; onDelete: (id: string) => void }): JSX.Element {
  return (
    <div className="rounded-lg border border-border bg-background/40 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="truncate text-sm font-semibold">{c.displayName}</h4>
            <Badge variant="outline" className="text-[10px] uppercase">
              {c.protocol}
            </Badge>
            {c.enabled ? (
              <Badge variant="success" className="text-[10px]">
                <CheckCircle2 className="-mt-0.5 mr-1 inline h-3 w-3" />
                Enabled
              </Badge>
            ) : (
              <Badge variant="warning" className="text-[10px]">
                <Clock className="-mt-0.5 mr-1 inline h-3 w-3" />
                Disabled
              </Badge>
            )}
          </div>
          {c.protocol === 'saml' && (
            <p className="mt-1 text-xs text-muted-foreground">
              <ShieldCheck className="-mt-0.5 mr-1 inline h-3 w-3" />
              Issuer: <code className="text-foreground/80">{c.samlIdpEntityId}</code>
            </p>
          )}
          {c.protocol === 'oidc' && (
            <p className="mt-1 text-xs text-muted-foreground">
              <KeyRound className="-mt-0.5 mr-1 inline h-3 w-3" />
              Discovery: <code className="text-foreground/80">{c.oidcDiscoveryUrl}</code>
            </p>
          )}
          {c.lastFailureMessage && (
            <p className="mt-1 text-xs text-red-500">
              <XCircle className="-mt-0.5 mr-1 inline h-3 w-3" />
              Last failure: {c.lastFailureMessage}
            </p>
          )}
        </div>
        <Button size="sm" variant="ghost" onClick={() => onDelete(c.id)} title="Delete">
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
      <div className="mt-3 flex items-center gap-2 text-[10px] text-muted-foreground">
        <span>Clock skew: {c.clockSkewMs}ms</span>
        <ChevronRight className="h-3 w-3 opacity-40" />
        <span>JIT: {c.jitProvisioning ? 'on' : 'off'}</span>
        <ChevronRight className="h-3 w-3 opacity-40" />
        <span>Tenant default: {c.defaultForTenant ? 'yes' : 'no'}</span>
      </div>
    </div>
  );
}

interface FormProps {
  submitting: boolean;
  setSubmitting: (b: boolean) => void;
  onDone: () => Promise<void>;
}

function SamlCreateForm({ submitting, setSubmitting, onDone }: FormProps): JSX.Element {
  const [displayName, setDisplayName] = useState('');
  const [idpEntityId, setIdpEntityId] = useState('');
  const [idpSsoUrl, setIdpSsoUrl] = useState('');
  const [idpCertPem, setIdpCertPem] = useState('');
  const [nameIdFormat, setNameIdFormat] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [signAuthnRequest, setSignAuthnRequest] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(): Promise<void> {
    setErr(null);
    setSubmitting(true);
    try {
      await createIdpConnection({
        protocol: 'saml',
        displayName,
        enabled,
        idpEntityId,
        idpSsoUrl,
        idpCertPem,
        ...(nameIdFormat ? { nameIdFormat } : {}),
        signAuthnRequest,
      });
      setDisplayName('');
      setIdpEntityId('');
      setIdpSsoUrl('');
      setIdpCertPem('');
      setNameIdFormat('');
      await onDone();
    } catch (e) {
      setErr(String((e as Error)?.message ?? e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-3">
      <Field label="Display name" value={displayName} onChange={setDisplayName} placeholder="e.g. Acme Bank Okta" />
      <Field label="IdP Entity ID" value={idpEntityId} onChange={setIdpEntityId} placeholder="https://idp.example.com/saml" />
      <Field label="IdP SSO URL (https://)" value={idpSsoUrl} onChange={setIdpSsoUrl} placeholder="https://idp.example.com/saml/sso" />
      <div>
        <label className="text-xs font-medium text-foreground/80">IdP signing certificate (PEM)</label>
        <textarea
          value={idpCertPem}
          onChange={(e) => setIdpCertPem(e.target.value)}
          rows={4}
          placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
          className="mt-1 w-full rounded-md border border-border bg-background/40 p-2 text-xs font-mono"
        />
        <p className="mt-1 text-[10px] text-muted-foreground">
          Server normalizes this - paste with or without markers, CRLF, etc.
        </p>
      </div>
      <Field
        label="NameID format (optional - leave empty to omit from AuthnRequest)"
        value={nameIdFormat}
        onChange={setNameIdFormat}
        placeholder="urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress"
      />
      <div className="flex items-center gap-4">
        <Checkbox label="Enabled" checked={enabled} onChange={setEnabled} />
        <Checkbox label="Sign AuthnRequest (requires SP keypair)" checked={signAuthnRequest} onChange={setSignAuthnRequest} />
      </div>
      {err && <p className="text-xs text-red-500">{err}</p>}
      <Button size="sm" onClick={submit} disabled={submitting || !displayName || !idpEntityId || !idpSsoUrl || !idpCertPem}>
        {submitting ? <Loader2 className="-mt-0.5 mr-1 inline h-3 w-3 animate-spin" /> : <Plus className="-mt-0.5 mr-1 inline h-3 w-3" />}
        Create SAML connection
      </Button>
    </div>
  );
}

function OidcCreateForm({ submitting, setSubmitting, onDone }: FormProps): JSX.Element {
  const [displayName, setDisplayName] = useState('');
  const [discoveryUrl, setDiscoveryUrl] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [redirectUri, setRedirectUri] = useState('');
  const [scopes, setScopes] = useState('openid profile email');
  const [enabled, setEnabled] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  async function submit(): Promise<void> {
    setErr(null);
    setSubmitting(true);
    try {
      await createIdpConnection({
        protocol: 'oidc',
        displayName,
        enabled,
        discoveryUrl,
        clientId,
        clientSecret,
        redirectUri,
        scopes,
      });
      setDisplayName('');
      setDiscoveryUrl('');
      setClientId('');
      setClientSecret('');
      setRedirectUri('');
      await onDone();
    } catch (e) {
      setErr(String((e as Error)?.message ?? e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-3">
      <Field label="Display name" value={displayName} onChange={setDisplayName} placeholder="e.g. Acme Bank Google" />
      <Field label="Discovery URL (.well-known/openid-configuration)" value={discoveryUrl} onChange={setDiscoveryUrl} placeholder="https://accounts.example.com/.well-known/openid-configuration" />
      <Field label="Client ID" value={clientId} onChange={setClientId} />
      <Field label="Client secret" value={clientSecret} onChange={setClientSecret} type="password" />
      <Field label="Redirect URI (this app's OIDC callback)" value={redirectUri} onChange={setRedirectUri} placeholder="https://<slug>.foctta.com/api/v1/auth/sso/oidc/callback/<tenantId>" />
      <Field label="Scopes" value={scopes} onChange={setScopes} />
      <p className="text-[10px] text-muted-foreground">
        PKCE-S256 enforced server-side per RFC 9700 - no client-side toggle.
      </p>
      <Checkbox label="Enabled" checked={enabled} onChange={setEnabled} />
      {err && <p className="text-xs text-red-500">{err}</p>}
      <Button size="sm" onClick={submit} disabled={submitting || !displayName || !discoveryUrl || !clientId || !clientSecret || !redirectUri}>
        {submitting ? <Loader2 className="-mt-0.5 mr-1 inline h-3 w-3 animate-spin" /> : <Plus className="-mt-0.5 mr-1 inline h-3 w-3" />}
        Create OIDC connection
      </Button>
    </div>
  );
}

function Field(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: 'text' | 'password' | 'url';
}): JSX.Element {
  return (
    <div>
      <label className="text-xs font-medium text-foreground/80">{props.label}</label>
      <input
        type={props.type ?? 'text'}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        className="mt-1 w-full rounded-md border border-border bg-background/40 p-2 text-xs"
      />
    </div>
  );
}

function Checkbox(props: { label: string; checked: boolean; onChange: (b: boolean) => void }): JSX.Element {
  return (
    <label className="inline-flex items-center gap-2 text-xs">
      <input type="checkbox" checked={props.checked} onChange={(e) => props.onChange(e.target.checked)} />
      {props.label}
    </label>
  );
}
